/**
 * Forecasts mini-section -- 3-up grid of MARS macro fan charts (GDP,
 * Inflation, T-Bill).
 *
 * Reuses the MARS Live feature's fan-chart pipeline (`fetchVintageMulti` ->
 * `buildFanTraces` / `buildFanLayout`) so the dashboard teaser renders
 * identically to the detail page at `/mars-live`.
 *
 * Cross-feature import note: this pulls helpers from `@/features/mars/live`.
 * The pipeline is dashboard-agnostic and could be promoted to a neutral
 * location, but doing so now would touch the live page unnecessarily.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import DashboardCard from '@/features/dashboard/components/DashboardCard';
import QueryState from '@/components/QueryState';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import {
  fetchVintageList,
  fetchVintageMulti,
  findPrevMonth,
  mlKeys,
} from '@/features/mars/live/api';
import {
  FAN_CHARTS,
  buildFanLayout,
  buildFanTraces,
  mergePreviousMonth,
  processVintageData,
} from '@/lib/plotly/fanChart';
import type { FanRecord } from '@/features/mars/live/types';

/** First three entries of FAN_CHARTS -- GDP, Inflation, T-Bill. */
const DASHBOARD_FAN_DEFS = FAN_CHARTS.slice(0, 3);

/**
 * Plotly config that strips the mode bar on the dashboard teaser. The full
 * `/mars-live` view leaves it in place; here we want the cleanest possible
 * thumbnail.
 */
const NO_MODEBAR = { displayModeBar: false } as const;

export default function ForecastsMiniSection(): JSX.Element {
  const vintagesQuery = useQuery({
    queryKey: mlKeys.vintagesList(),
    queryFn: fetchVintageList,
    staleTime: 5 * 60 * 1000,
  });

  const latestVintage = useMemo<string | null>(() => {
    const list = vintagesQuery.data;
    if (!list || list.length === 0) return null;
    return list[list.length - 1];
  }, [vintagesQuery.data]);

  const currentQuery = useQuery({
    queryKey: latestVintage
      ? mlKeys.vintageMulti(latestVintage)
      : (['dashboard', 'forecasts', '__placeholder__'] as const),
    queryFn: () => fetchVintageMulti(latestVintage ?? ''),
    enabled: latestVintage !== null,
    staleTime: 60_000,
  });

  const prevDate = useMemo(
    () =>
      latestVintage && vintagesQuery.data
        ? findPrevMonth(vintagesQuery.data, latestVintage)
        : null,
    [latestVintage, vintagesQuery.data],
  );

  const prevQuery = useQuery({
    queryKey: prevDate
      ? mlKeys.vintageMulti(prevDate)
      : (['dashboard', 'forecasts', '__placeholder-prev__'] as const),
    queryFn: () => fetchVintageMulti(prevDate ?? ''),
    enabled: prevDate !== null,
    staleTime: 60_000,
  });

  const charts = useMemo(() => {
    const current = currentQuery.data;
    if (!current) return [];
    return DASHBOARD_FAN_DEFS.map((def) => {
      const rawCurrent: FanRecord[] = current[def.variable] ?? [];
      const rawPrev: FanRecord[] | null = prevQuery.data
        ? prevQuery.data[def.variable] ?? null
        : null;
      const merged = mergePreviousMonth(rawCurrent, rawPrev);
      const processed = processVintageData(merged);
      const traces = buildFanTraces(processed, def);
      // Pass null for the fixed range so `buildFanLayout` falls through to
      // `computeYRange(processed)` — tight to the inner band (p16/p50/p84) +
      // actual, not the backend-wide outer range that spans all vintages.
      const layout = buildFanLayout(def, processed, null, null, { showCursorLine: false });
      return { def, traces, layout };
    });
  }, [currentQuery.data, prevQuery.data]);

  // Two queries feed this teaser; collapse them into one state object so the
  // shared QueryState wrapper can render the loading/error/empty triad.
  const gridQuery = {
    isLoading: vintagesQuery.isLoading || currentQuery.isLoading,
    isError: Boolean(vintagesQuery.error || currentQuery.error),
    data: charts,
  };

  return (
    <DashboardCard title="Macro Forecasts" href="/forecasts/macro_economic" hrefLabel="Know More">
      <QueryState
        query={gridQuery}
        isEmpty={(c) => c.length === 0}
        loadingLabel="Loading"
        errorLabel="Failed to load forecasts."
        emptyLabel="No forecast data available."
      >
        {(builtCharts) => (
          /* Responsive auto-fit grid: min 350px columns, 0.5rem gap. */
          <div className="grid grid-cols-[repeat(auto-fit,minmax(350px,1fr))] gap-2">
            {builtCharts.map(({ def, traces, layout }) => (
              <div
                key={def.variable}
                /* 400px tall: enough for the title row + horizontal legend +
                   plot area + x-axis row. A shorter height clips the title and
                   legend and shifts every section below up. */
                className="overflow-hidden flex flex-col"
                style={{
                  height: 400,
                  borderRadius: 6,
                  background: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                }}
              >
                <PlotlyChart data={traces} layout={layout} config={NO_MODEBAR} />
              </div>
            ))}
          </div>
        )}
      </QueryState>
    </DashboardCard>
  );
}
